import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";

export interface User {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => void;
  adminLogin: (
    email: string,
    password: string,
    setError: (err: string) => void,
    setLoading: (loading: boolean) => void
  ) => void;
  signup: (name: string, email: string, password: string) => void;
  logout: () => void;
  forgotPassword: (email: string) => void;
  resetPassword: (newPassword: string) => void;
  getAllUsers: () => Promise<any>;
  createModule: (
    title: string,
    description: string,
    access_level: string,
    module_id: string
  ) => any;
  getModule: () => any;
  addModule: (
    module_id: string,
    id: string,
    title: string,
    description: string,
    type: string,
    access_level: string,
    url: string,
    duration: string,
    thumbnail_url: string
  ) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const MOCK_USERS: User[] = [
  {
    id: "1",
    email: "demo@example.com",
    name: "Demo User",
    // memberships: ["course"],
    // locked: false,
    isAdmin: false,
    // courseProgress: [
    //   {
    //     courseId: "getting-started",
    //     courseName: "Getting Started",
    //     completedItems: 1,
    //     totalItems: 2,
    //     percentage: 50,
    //   },
    // ],
  },
  {
    id: "2",
    email: "support@applaunchai.com",
    name: "Admin User",
    // memberships: ["all"],
    // locked: false,
    isAdmin: true,
  },
];

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const { toast } = useToast();
  useEffect(() => {
    (async () => {
      const session = await supabase.auth.getUser();
      if (session.data.user) {
        setUser({
          name: session.data.user.user_metadata.full_name,
          email: session.data.user.email,
          isAdmin: session?.data.user.user_metadata?.role === "admin",
          id: session.data.user.id,
        });
      }
      console.log(session, "session");
    })();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (error) throw error;
      console.log(data, "login data");
      // Get profile data
      // const { data: profileData } = await supabase
      //   .from("profiles")
      //   .select("full_name, role")
      //   .eq("user_id", data.user.id)
      //   .single();
      if (!data.user) {
        throw new Error("User not found");
      }
      setUser({
        id: data.user.id,
        email: data.user.email,
        name: data.user.user_metadata.full_name,
        isAdmin: false,
      });
      return {
        data: {
          user: data.user,
          session: data.session,
        },
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error("Login error:", error);
      return {
        data: { user: null, session: null },
        error: error as Error,
        status: 500,
      };
    }
  };
  const getLatestData = () => {
    // Load all users from localStorage if available
    const storedAllUsers = localStorage.getItem("allUsers");
    const latestUsers = storedAllUsers
      ? JSON.parse(storedAllUsers)
      : MOCK_USERS;

    // Load deleted emails from localStorage
    const storedDeletedEmails = localStorage.getItem("deletedEmails");
    const latestDeletedEmails = storedDeletedEmails
      ? JSON.parse(storedDeletedEmails)
      : [];

    // Load purchase records from localStorage
    const storedPurchaseRecords = localStorage.getItem("purchaseRecords");
    const latestPurchaseRecords = storedPurchaseRecords
      ? JSON.parse(storedPurchaseRecords)
      : [];

    return { latestUsers, latestDeletedEmails, latestPurchaseRecords };
  };

  const adminLogin = async (
    email: string,
    password: string,
    setError: (err: string) => void,
    setLoading: (loading: boolean) => void
  ) => {
    const { data, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    console.log(authError, "authError");
    if (authError) {
      setError(authError.message);
      setLoading(false);
      return;
    }
    if (!data.user) {
      setError("No user returned");
      setLoading(false);
      return;
    }
    console.log(data, "admin user data");
    // Check app_metadata.role === 'admin'
    const role = data.user.user_metadata?.role;
    if (role !== "admin") {
      setError("You are not an admin");
      await supabase.auth.signOut();
      setLoading(false);
      return;
    }
    setUser({
      id: data.user.id,
      email: data.user.email,
      name: data.user.user_metadata.full_name,
      isAdmin: true,
    });
    // try {
    //   // Get latest data
    //   const { latestUsers, latestDeletedEmails } = getLatestData();

    //   // Normalize email for case-insensitive comparison
    //   const normalizedEmail = email.toLowerCase().trim();

    //   // Check if email is in deleted list
    //   const isDeleted = latestDeletedEmails.some(
    //     (d) => d.email.toLowerCase() === normalizedEmail
    //   );

    //   if (isDeleted) {
    //     toast({
    //       title: "Account Deleted",
    //       description: "This admin account has been deleted.",
    //       variant: "destructive",
    //     });
    //     throw new Error("Admin account has been deleted");
    //   }

    //   // Find admin user
    //   const adminUser = latestUsers.find(
    //     (u) => u.isAdmin && u.email.toLowerCase() === normalizedEmail
    //   );

    //   if (adminUser) {
    //     // Get user passwords from localStorage
    //     const userPasswords = JSON.parse(
    //       localStorage.getItem("userPasswords") || "{}"
    //     );
    //     const storedPassword = userPasswords[normalizedEmail];

    //     // Check if admin has a specific password set or use default admin password
    //     if (storedPassword && password === storedPassword) {
    //       // User has a custom password and it matches
    //     } else if (!storedPassword && password === "Neverguess6249") {
    //       // No custom password, use default admin password
    //     } else {
    //       toast({
    //         title: "Admin Login Failed",
    //         description: "Invalid admin credentials",
    //         variant: "destructive",
    //       });
    //       throw new Error("Invalid admin credentials");
    //     }

    //     setUser(adminUser);
    //     localStorage.setItem("user", JSON.stringify(adminUser));
    //     toast({
    //       title: "Admin Login Successful",
    //       description: "Welcome to the admin panel!",
    //     });
    //   } else {
    //     toast({
    //       title: "Admin Login Failed",
    //       description: "Invalid admin credentials",
    //       variant: "destructive",
    //     });
    //     throw new Error("Invalid admin credentials");
    //   }
    // } catch (error) {
    //   console.error("Admin login error:", error);
    //   throw error;
    // }
  };
  const signup = async (fullName: string, email: string, password: string) => {
    try {
      if (!fullName) {
        throw new Error("Full name is required");
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
          data: {
            full_name: fullName, // Use snake_case key
            // role: "admin",
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error("User creation failed");

      toast({
        title: "Signup Successful",
        description: "Verification email sent. Please check your inbox.",
      });
      return {
        data: {
          user: {
            ...authData.user,
            user_metadata: {
              ...authData.user.user_metadata,
              full_name: fullName,
            },
          },
          session: authData.session,
        },
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error("Signup error:", error);
      return {
        data: { user: null, session: null },
        error: error as Error,
        status: 500,
      };
    }
  };
  const getAllUsers = async () => {
    // const {
    //   data: { users },
    //   error,
    // } = await supabase.auth.admin.listUser();
    // console.log(users, error, "getusers from db");
    const { data: users, error } = await supabase.from("users").select('*');
    console.log(users, error, 'getallusers from db')
    if (error) {
      console.log("Error loading users:", error);
      toast({
        title: "Error",
        description: "No Users found",
        variant: "destructive",
      });
      return [];
    } else {
      return users;
    }
  };

  const logout = async () => {
    try {
      setUser(null);
      localStorage.removeItem("user");
      await supabase.auth.signOut();
      toast({
        title: "Logged out",
        description: "You have been logged out successfully.",
      });
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: "http://localhost:8080/reset-password", // e.g., 'http://localhost:3000/reset-password'
      });

      if (error) throw error;

      return {
        data: { user: null, session: null },
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error("Password reset error:", error);
      return {
        data: { user: null, session: null },
        error: error as Error,
        status: 500,
      };
    }
  };
  const resetPassword = async (newPassword: string) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      return {
        data: {
          user: data.user,
          session: null,
        },
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error("Password update error:", error);
      return {
        data: { user: null, session: null },
        error: error as Error,
        status: 500,
      };
    }
  };

  async function createModule(
    title: string,
    description: string,
    access_level: string,
    module_id: string
  ) {
    const { data, error } = await supabase
      .from("modules")
      .insert([{ title, description, access_level, module_id }])
      .select();

    if (error) throw error;
    return data;
  }

  async function getModule() {
    const { data, error } = await supabase.from("modules").select(`  
    * 
    `);
    console.log(data, "get module data");
    if (error) throw error;
    return data;
  }
  async function addModule(
    module_id: string,
    id: string,
    title: string,
    description: string,
    type: string,
    access_level: string,
    url: string,
    duration: string,
    thumbnail_url: string
  ) {
    const { data, error } = await supabase
      .from("modules")
      .select("items")
      .eq("module_id", module_id)
      .single();
    console.log(error, data, "add module item data");
    if (error) {
      throw error;
    }
    const existing: any[] = data?.items || [];
    const updated = [
      ...existing,
      {
        module_id: module_id,
        id: id,
        title,
        description,
        type,
        access_level,
        url,
        duration,
        thumbnail_url,
      },
    ];
    const { data: updatedData, error: updateError } = await supabase
      .from("modules")
      .update({ items: updated })
      .eq("module_id", module_id);
      if(updateError) throw updateError;
    return updatedData;
  }
  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        adminLogin,
        signup,
        logout,
        forgotPassword,
        resetPassword,
        getAllUsers,
        createModule,
        getModule,
        addModule,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
